import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { AlertTriangle, Plus, X, Camera } from "lucide-react";
import { Badge } from "../../components/ui/badge";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function OpsIncidents({ user }) {
  const { toast } = useToast();
  const [locations, setLocations] = useState({ kitchen: null, outlets: [] });
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  
  const [form, setForm] = useState({
    outlet_id: "",
    severity: "medium",
    description: "",
    photo_urls: []
  });
  const [photoInput, setPhotoInput] = useState("");

  useEffect(() => {
    if (user?.user_id) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const locRes = await fetch(`${API_URL}/api/ops-extended/my-locations/${user.user_id}`, { credentials: "include" });
      if (locRes.ok) {
        setLocations(await locRes.json());
      }
      
      const incRes = await fetch(`${API_URL}/api/ops-extended/incidents?ops_manager_id=${user.user_id}`, { credentials: "include" });
      if (incRes.ok) setIncidents(await incRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPhoto = () => {
    if (photoInput.trim()) {
      setForm({ ...form, photo_urls: [...form.photo_urls, photoInput.trim()] });
      setPhotoInput("");
    }
  };

  const handleRemovePhoto = (index) => {
    setForm({ ...form, photo_urls: form.photo_urls.filter((_, i) => i !== index) });
  };

  const handleSubmit = async () => {
    if (!form.outlet_id || !form.description) {
      toast({ title: "Error", description: "Please fill all required fields", variant: "destructive" });
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/ops-extended/incidents?ops_manager_id=${user.user_id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form)
      });
      
      if (res.ok) {
        toast({ title: "Incident report filed successfully", description: "Email notification sent to management" });
        setShowForm(false);
        setForm({ outlet_id: "", severity: "medium", description: "", photo_urls: [] });
        fetchData();
      } else {
        const error = await res.json();
        toast({ title: "Error", description: error.detail, variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to file incident", variant: "destructive" });
    }
  };

  const getOutletName = (outletId) => {
    const outlet = locations.outlets.find(o => o.outlet_id === outletId);
    return outlet ? outlet.name : "Unknown";
  };

  const getSeverityColor = (severity) => {
    const colors = {
      low: "bg-gray-500",
      medium: "bg-yellow-500",
      high: "bg-orange-500",
      critical: "bg-red-500"
    };
    return colors[severity] || "bg-gray-500";
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  if (!locations.kitchen) {
    return (
      <Card data-testid="ops-incidents">
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <AlertTriangle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">No Kitchen Assigned</h3>
            <p className="text-muted-foreground">Contact your administrator to assign you a kitchen.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="ops-incidents">
      {/* File Incident */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Incident Reports
          </CardTitle>
          {!showForm && (
            <Button onClick={() => setShowForm(true)} variant="destructive">
              <Plus className="h-4 w-4 mr-2" /> File Incident
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {showForm && (
            <div className="border border-red-200 rounded-lg p-4 mb-6 space-y-4 bg-red-50">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-red-700">File New Incident Report</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Outlet *</Label>
                  <Select value={form.outlet_id} onValueChange={(v) => setForm({ ...form, outlet_id: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select outlet" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.outlets.map((o) => (
                        <SelectItem key={o.outlet_id} value={o.outlet_id}>{o.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Severity *</Label>
                  <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Description *</Label>
                <Textarea 
                  value={form.description} 
                  onChange={(e) => setForm({ ...form, description: e.target.value })} 
                  placeholder="Describe the incident in detail..."
                  rows={4}
                />
              </div>

              {/* Photo URLs */}
              <div>
                <Label className="flex items-center gap-2">
                  <Camera className="h-4 w-4" /> Photo URLs
                </Label>
                <div className="flex gap-2 mt-2">
                  <Input 
                    value={photoInput}
                    onChange={(e) => setPhotoInput(e.target.value)}
                    placeholder="Paste image URL"
                    className="flex-1"
                  />
                  <Button type="button" variant="outline" onClick={handleAddPhoto}>Add</Button>
                </div>
                {form.photo_urls.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {form.photo_urls.map((url, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm bg-white p-2 rounded">
                        <span className="flex-1 truncate">{url}</span>
                        <Button variant="ghost" size="sm" onClick={() => handleRemovePhoto(idx)}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Button onClick={handleSubmit} className="w-full" variant="destructive">
                Submit Incident Report
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                An email will be sent to management upon submission
              </p>
            </div>
          )}

          {/* Incident History */}
          <div>
            <h3 className="font-medium mb-3">My Incident Reports</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Outlet</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {incidents.map((incident) => (
                  <TableRow key={incident.incident_id}>
                    <TableCell className="text-sm">
                      {new Date(incident.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="font-medium">{getOutletName(incident.outlet_id)}</TableCell>
                    <TableCell>
                      <Badge className={getSeverityColor(incident.severity)}>
                        {incident.severity}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm truncate max-w-xs">{incident.description}</p>
                      {incident.photo_urls?.length > 0 && (
                        <span className="text-xs text-muted-foreground">
                          {incident.photo_urls.length} photo(s)
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{incident.status}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
                {incidents.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground">
                      No incident reports filed
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
